import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Users, AlertTriangle, UserX, Clock, Shield, CreditCard, User, Building, Banknote, Building2, Mail, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList } from 'recharts';
import api from '../services/api';
import ExitComparisonChart from '../components/dashboard/ExitComparisonChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

const HRReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('mbj@seeregroup.com');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const sendReportEmail = async () => {
    setEmailSending(true);
    try {
      const res = await api.post('/api/document-email/hr-report', {
        year: selectedYear,
        month: selectedMonth,
        to_email: emailTo || undefined,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
      });
      toast({ title: 'Report emailed', description: res.data?.message || 'HR Report sent.' });
      setEmailOpen(false);
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setEmailSending(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/hr-report/data/${selectedYear}/${selectedMonth}`);
      setReportData(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load report data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/api/hr-report/pdf/${selectedYear}/${selectedMonth}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HR_Report_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'Success', description: 'Report downloaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download report', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [selectedYear, selectedMonth]);

  const CustomBarLabel = ({ x, y, width, height, value }) => {
    return (
      <text x={x + width / 2} y={y + height / 2} fill="white" textAnchor="middle" dominantBaseline="middle" fontSize={12} fontWeight="bold">
        {value}
      </text>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading report...</div>;
  }

  return (
    <div className="space-y-6 pb-8" data-testid="hr-report-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold">HR Report</h1>
          <p className="text-gray-600 mt-1">Comprehensive monthly HR analytics</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-40" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={downloadPDF} disabled={downloading} data-testid="download-report-btn">
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button onClick={() => { setEmailSubject(''); setEmailMessage(''); setEmailOpen(true); }} variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="email-report-btn">
            <Mail className="h-4 w-4 mr-2" />
            Email Report
          </Button>
        </div>
      </div>

      {reportData && (
        <>
          {/* Key Metrics Bar */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                Key Metrics - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={[
                      { name: 'Total Employees', value: reportData.key_metrics.total_employees, fill: '#3b82f6' },
                      { name: 'On Leave', value: reportData.key_metrics.employees_on_leave, fill: '#10b981' },
                      { name: 'New Joiners', value: reportData.key_metrics.new_joiners, fill: '#f59e0b' }
                    ]}
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} label={CustomBarLabel}>
                      {[0, 1, 2].map((_, index) => (
                        <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b'][index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-center">
                <span className="text-2xl font-bold text-green-600">AED {reportData.key_metrics.total_salary?.toLocaleString()}</span>
                <span className="text-gray-500 ml-2">Total Monthly Salary</span>
              </div>
            </CardContent>
          </Card>

          {/* Row 1: Department Distribution & Gender */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Department Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building className="h-5 w-5 text-green-600" />
                  Department Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.department_distribution?.slice(0, 8)} layout="vertical" margin={{ left: 80, right: 30 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="value" position="right" style={{ fill: '#10b981', fontWeight: 'bold', fontSize: 12 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gender Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5 text-purple-600" />
                  Gender Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.gender_distribution} margin={{ left: 20, right: 20 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis hide />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} label={CustomBarLabel}>
                        {reportData.gender_distribution?.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : '#ec4899'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resignations vs Terminations — Year over Year */}
          <ExitComparisonChart year={selectedYear} />

          {/* Row 2: Employee Status & Currently on Leave */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employee Status */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5 text-amber-600" />
                  Employee Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.employee_status} margin={{ left: 20, right: 20 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis hide />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} label={CustomBarLabel}>
                        {reportData.employee_status?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={
                            entry.name === 'Active' ? '#10b981' :
                            entry.name === 'Suspended' ? '#f59e0b' :
                            entry.name === 'Resigned' ? '#6b7280' :
                            '#ef4444'
                          } />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Currently on Leave */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="h-5 w-5 text-cyan-600" />
                  Currently on Leave ({reportData.currently_on_leave?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {reportData.currently_on_leave?.length > 0 ? (
                    reportData.currently_on_leave.slice(0, 5).map((leave, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-cyan-50 rounded text-sm">
                        <div>
                          <span className="font-medium">{leave.employee_name}</span>
                          <span className="text-gray-500 ml-2">({leave.department})</span>
                        </div>
                        <Badge variant="outline" className="bg-cyan-100">{leave.leave_type}</Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No employees currently on leave</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Employees on Probation */}
            <Card className="border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                  On Probation ({reportData.probation_employees?.length || 0})
                </CardTitle>
                <p className="text-xs text-amber-600">Employees within first 6 months - leaves treated as unpaid</p>
              </CardHeader>
              <CardContent>
                {reportData.probation_employees?.length > 0 ? (
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="bg-amber-50 text-amber-800">
                          <th className="text-left p-2">Employee</th>
                          <th className="text-left p-2">Dept</th>
                          <th className="text-left p-2">Position</th>
                          <th className="text-left p-2">Join Date</th>
                          <th className="text-center p-2">Days Worked</th>
                          <th className="text-center p-2">Days Left</th>
                          <th className="text-left p-2">Probation Ends</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.probation_employees.map((emp, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-amber-50/50' : 'bg-white'}>
                            <td className="p-2">
                              <span className="font-medium">{emp.employee_name}</span>
                              <span className="text-gray-500 text-xs ml-1">({emp.employee_code})</span>
                            </td>
                            <td className="p-2">{emp.department}</td>
                            <td className="p-2 text-gray-600">{emp.position}</td>
                            <td className="p-2">{emp.join_date}</td>
                            <td className="p-2 text-center">{emp.days_in_service}</td>
                            <td className="p-2 text-center">
                              <Badge className={emp.days_remaining <= 30 ? 'bg-green-500' : 'bg-amber-500'}>
                                {emp.days_remaining}d
                              </Badge>
                            </td>
                            <td className="p-2">{emp.probation_end_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No employees currently on probation</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Resigned/Terminated & Suspensions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Resigned/Terminated (last 45 days) */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UserX className="h-5 w-5 text-red-600" />
                  Resigned/Terminated (Last 45 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {reportData.resigned_terminated?.length > 0 ? (
                    reportData.resigned_terminated.map((emp, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded text-sm">
                        <div>
                          <span className="font-medium">{emp.employee_name}</span>
                          <span className="text-gray-500 ml-2">({emp.employee_code})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{emp.exit_date}</span>
                          <Badge variant={emp.status === 'Resigned' ? 'secondary' : 'destructive'}>{emp.status}</Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent resignations or terminations</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Active Suspensions & Warnings */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-orange-600" />
                  Suspensions & Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {reportData.active_suspensions?.map((susp, idx) => (
                    <div key={`susp-${idx}`} className="flex justify-between items-center p-2 bg-orange-50 rounded text-sm">
                      <div>
                        <span className="font-medium">{susp.employee_name}</span>
                        <span className="text-gray-500 ml-2">({susp.days} days)</span>
                      </div>
                      <Badge className="bg-orange-500">Suspended until {susp.end_date}</Badge>
                    </div>
                  ))}
                  {reportData.active_warnings?.slice(0, 3).map((warn, idx) => (
                    <div key={`warn-${idx}`} className="flex justify-between items-center p-2 bg-yellow-50 rounded text-sm">
                      <div>
                        <span className="font-medium">{warn.employee_name}</span>
                      </div>
                      <Badge className="bg-yellow-500">Warning valid until {warn.validity_date}</Badge>
                    </div>
                  ))}
                  {(!reportData.active_suspensions?.length && !reportData.active_warnings?.length) && (
                    <p className="text-gray-500 text-center py-4">No active suspensions or warnings</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 4: Visa Renewals */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-blue-600" />
                Upcoming Visa Renewals (Next 60 Days) - {reportData.upcoming_visa_renewals?.length || 0} employees
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-48 overflow-y-auto">
                {reportData.upcoming_visa_renewals?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {reportData.upcoming_visa_renewals.map((visa, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-blue-50 rounded text-sm">
                        <div>
                          <span className="font-medium">{visa.employee_name}</span>
                          <span className="text-gray-500 block text-xs">{visa.visa_company}</span>
                        </div>
                        <Badge variant={visa.days_until_expiry <= 30 ? 'destructive' : 'outline'}>
                          {visa.days_until_expiry} days
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No visa renewals due in the next 60 days</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* EOSB Payment Status Section */}
          {(reportData.eosb_status?.details?.length > 0 || reportData.eosb_status?.total_liability > 0) && (
            <Card className="border-emerald-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                    EOSB Payment Status
                  </div>
                  <div className="flex gap-3 text-sm">
                    <Badge className="bg-red-500">{reportData.eosb_status.pending || 0} Pending</Badge>
                    <Badge className="bg-amber-500">{reportData.eosb_status.partial || 0} Partial</Badge>
                    <Badge className="bg-green-500">{reportData.eosb_status.paid || 0} Paid</Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <p className="text-xs text-red-600">Exited - Pending</p>
                    <p className="text-xl font-bold text-red-700">AED {(reportData.eosb_status.pending_amount || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <p className="text-xs text-green-600">Exited - Paid</p>
                    <p className="text-xl font-bold text-green-700">AED {(reportData.eosb_status.paid_amount || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-600">Active Staff Liability</p>
                    <p className="text-xl font-bold text-blue-700">AED {(reportData.eosb_status.total_liability || 0).toLocaleString()}</p>
                  </div>
                </div>
                
                {/* Details Table */}
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="bg-emerald-50 text-emerald-800">
                        <th className="text-left p-2">Employee</th>
                        <th className="text-left p-2">Dept</th>
                        <th className="text-left p-2">Exit Date</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-right p-2">Paid</th>
                        <th className="text-right p-2">Remaining</th>
                        <th className="text-center p-2">Installments</th>
                        <th className="text-center p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.eosb_status.details.map((eosb, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-2">
                            <span className="font-medium">{eosb.employee_name}</span>
                            <span className="text-gray-500 text-xs ml-1">({eosb.employee_code})</span>
                          </td>
                          <td className="p-2 text-gray-600">{eosb.department}</td>
                          <td className="p-2 text-gray-600">{eosb.exit_date}</td>
                          <td className="p-2 text-right">{eosb.total_amount.toLocaleString()}</td>
                          <td className="p-2 text-right text-green-600">{eosb.paid_amount.toLocaleString()}</td>
                          <td className="p-2 text-right text-red-600">{eosb.remaining_amount.toLocaleString()}</td>
                          <td className="p-2 text-center">{eosb.installments_count || 0}</td>
                          <td className="p-2 text-center">
                            <Badge 
                              className={
                                eosb.status === 'paid' ? 'bg-green-500' : 
                                eosb.status === 'partial' ? 'bg-amber-500' : 'bg-red-500'
                              }
                            >
                              {eosb.status === 'paid' ? 'Paid' : eosb.status === 'partial' ? 'Partial' : 'Pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Salary Advances Summary */}
          {(reportData.advances_summary?.total_advances > 0 || reportData.advances_summary?.total_outstanding > 0) && (
            <Card className="border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    Salary Advances Summary
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/advances/export/summary?format=pdf`);
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'Advances_Summary.pdf';
                          a.click();
                        } catch (error) {
                          console.error('Download failed', error);
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/advances/export/summary?format=excel`);
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'Advances_Summary.xlsx';
                          a.click();
                        } catch (error) {
                          console.error('Download failed', error);
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" /> Excel
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-600">Total Advances</p>
                    <p className="text-xl font-bold text-blue-700">{reportData.advances_summary.total_advances || 0}</p>
                    <p className="text-xs text-gray-500">{reportData.advances_summary.employees_with_advances || 0} employees</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                    <p className="text-xs text-indigo-600">Total Advanced</p>
                    <p className="text-xl font-bold text-indigo-700">AED {(reportData.advances_summary.total_advanced || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <p className="text-xs text-green-600">Total Repaid</p>
                    <p className="text-xl font-bold text-green-700">AED {(reportData.advances_summary.total_repaid || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <p className="text-xs text-red-600">Outstanding</p>
                    <p className="text-xl font-bold text-red-700">AED {(reportData.advances_summary.total_outstanding || 0).toLocaleString()}</p>
                  </div>
                </div>

                {/* By Type */}
                {reportData.advances_summary.by_type && Object.keys(reportData.advances_summary.by_type).length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">By Advance Type</p>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(reportData.advances_summary.by_type).map(([type, data]) => (
                        <div key={type} className="bg-gray-50 rounded-lg p-2 border">
                          <p className="text-xs font-medium text-gray-600 capitalize">{type.replace('_', ' ')}</p>
                          <div className="flex justify-between text-xs mt-1">
                            <span>{data.count} advances</span>
                            <span className="text-red-600">AED {data.outstanding?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outstanding Advances Table */}
                {reportData.advances_summary.details?.length > 0 && (
                  <div className="overflow-x-auto">
                    <p className="text-sm font-medium text-gray-700 mb-2">Outstanding Advances</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-red-100 text-red-800">
                          <th className="p-2 text-left">Employee</th>
                          <th className="p-2 text-left">Type</th>
                          <th className="p-2 text-right">Amount</th>
                          <th className="p-2 text-right">Repaid</th>
                          <th className="p-2 text-right">Outstanding</th>
                          <th className="p-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.advances_summary.details.slice(0, 10).map((adv, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="p-2">
                              <div className="font-medium">{adv.employee_name}</div>
                              <div className="text-xs text-gray-500">{adv.employee_code}</div>
                            </td>
                            <td className="p-2 capitalize">{adv.advance_type?.replace('_', ' ')}</td>
                            <td className="p-2 text-right">AED {adv.amount?.toLocaleString()}</td>
                            <td className="p-2 text-right text-green-600">AED {adv.repaid?.toLocaleString()}</td>
                            <td className="p-2 text-right text-red-600 font-medium">AED {adv.outstanding?.toLocaleString()}</td>
                            <td className="p-2 text-center">
                              <Badge 
                                className={
                                  adv.status === 'paid' ? 'bg-green-500' : 
                                  adv.status === 'partially_paid' ? 'bg-amber-500' : 'bg-blue-500'
                                }
                              >
                                {adv.status?.replace('_', ' ')}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {reportData.advances_summary.details.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Showing top 10 of {reportData.advances_summary.details.length} outstanding advances
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Row 5: Salary by Payment Method & Pie Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Salary by Payment Method */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5 text-violet-600" />
                  Monthly Salary by Payment Method (AED)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.salary_by_payment_method} layout="vertical" margin={{ left: 80, right: 60 }}>
                      <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip formatter={(v) => `AED ${v.toLocaleString()}`} />
                      <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                        {reportData.salary_by_payment_method?.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                        <LabelList dataKey="value" position="right" formatter={(v) => `${(v/1000).toFixed(0)}K`} style={{ fontSize: 11, fontWeight: 'bold' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Salary by Payroll Company Pie Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Banknote className="h-5 w-5 text-green-600" />
                  Salary by Payroll Company (AED)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={reportData.salary_by_company}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {reportData.salary_by_company?.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `AED ${v.toLocaleString()}`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 6: Visa Distribution & EOSB Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Visa Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building className="h-5 w-5 text-teal-600" />
                  Visa Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.visa_distribution} layout="vertical" margin={{ left: 80, right: 30 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#14b8a6" radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="value" position="right" style={{ fill: '#14b8a6', fontWeight: 'bold', fontSize: 12 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* EOSB Status */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Banknote className="h-5 w-5 text-red-600" />
                  EOSB Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-around items-center h-40">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-red-600">{reportData.eosb_status?.pending || 0}</div>
                    <div className="text-gray-500">Pending</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600">{reportData.eosb_status?.paid || 0}</div>
                    <div className="text-gray-500">Paid</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cost to Company Section */}
          <Card className="border-orange-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg text-orange-700">
                <Banknote className="h-5 w-5" />
                Cost to Company by Department (Monthly)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData.cost_by_department?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-100 text-orange-800">
                        <th className="text-left p-2">Department</th>
                        <th className="text-right p-2">Employees</th>
                        <th className="text-right p-2">Salary (AED)</th>
                        <th className="text-right p-2">Overhead (AED)</th>
                        <th className="text-right p-2 font-bold">Total (AED)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.cost_by_department.map((dept, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-orange-50' : 'bg-white'}>
                          <td className="p-2 font-medium">{dept.department}</td>
                          <td className="p-2 text-right">{dept.count}</td>
                          <td className="p-2 text-right">{dept.salary.toLocaleString()}</td>
                          <td className="p-2 text-right">{dept.overhead.toLocaleString()}</td>
                          <td className="p-2 text-right font-bold text-orange-700">{dept.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-orange-200 font-bold text-orange-900">
                        <td className="p-2">TOTAL</td>
                        <td className="p-2 text-right">{reportData.cost_by_department.reduce((sum, d) => sum + d.count, 0)}</td>
                        <td className="p-2 text-right">{reportData.total_cost_to_company?.salary?.toLocaleString()}</td>
                        <td className="p-2 text-right">{reportData.total_cost_to_company?.overhead?.toLocaleString()}</td>
                        <td className="p-2 text-right text-lg">{reportData.total_cost_to_company?.total?.toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No cost data available</p>
              )}
            </CardContent>
          </Card>

          {/* Employee Cost Breakdown */}
          {reportData.employee_cost_breakdown?.length > 0 && (
            <Card className="border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-purple-700">
                  <CreditCard className="h-5 w-5" />
                  Employee Cost Breakdown (Monthly)
                </CardTitle>
                <p className="text-xs text-gray-500">Showing employees with overhead costs entered (includes dependent insurance)</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="bg-purple-100 text-purple-800">
                        <th className="text-left p-2">Employee</th>
                        <th className="text-left p-2">Dept</th>
                        <th className="text-right p-2">Salary</th>
                        <th className="text-right p-2">Visa</th>
                        <th className="text-right p-2">Insurance</th>
                        <th className="text-right p-2">Dep.Ins</th>
                        <th className="text-right p-2">Uniform</th>
                        <th className="text-right p-2">Accom.</th>
                        <th className="text-right p-2">Ticket</th>
                        <th className="text-right p-2 font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.employee_cost_breakdown.map((emp, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-purple-50' : 'bg-white'}>
                          <td className="p-2 font-medium">{emp.employee_name}</td>
                          <td className="p-2">{emp.department}</td>
                          <td className="p-2 text-right">{emp.monthly_salary.toLocaleString()}</td>
                          <td className="p-2 text-right">{emp.visa_monthly.toFixed(0)}</td>
                          <td className="p-2 text-right">{emp.insurance_monthly.toFixed(0)}</td>
                          <td className="p-2 text-right">{(emp.dependent_insurance_monthly || 0).toFixed(0)}</td>
                          <td className="p-2 text-right">{emp.uniform_monthly.toFixed(0)}</td>
                          <td className="p-2 text-right">{emp.accommodation_monthly.toFixed(0)}</td>
                          <td className="p-2 text-right">
                            <span title={`Frequency: ${emp.ticket_frequency === '1_year' ? '1 Year' : '2 Years'}`}>
                              {emp.ticket_monthly.toFixed(0)}
                              {emp.ticket_frequency === '1_year' && <span className="text-xs text-blue-600 ml-1">(1Y)</span>}
                            </span>
                          </td>
                          <td className="p-2 text-right font-bold text-purple-700">{emp.total_monthly.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Company Documents Expiry Reminder Section */}
          {(reportData.company_docs_expired?.length > 0 || reportData.company_docs_expiring?.length > 0) && (
            <Card className="border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-purple-700">
                  <Building2 className="h-5 w-5" />
                  Company Documents - 45 Days Reminder
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Expired Documents */}
                  {reportData.company_docs_expired?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-700 flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-4 w-4" />
                        Expired Documents ({reportData.company_docs_expired.length})
                      </h4>
                      <div className="space-y-2">
                        {reportData.company_docs_expired.map((doc, idx) => (
                          <div key={idx} className="bg-white rounded p-3 border border-red-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{doc.document_type}</p>
                                <p className="text-sm text-gray-600">{doc.company_name}</p>
                                {doc.document_number && (
                                  <p className="text-xs text-gray-500">#{doc.document_number}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <Badge className="bg-red-500">Expired {Math.abs(doc.days_until_expiry)}d ago</Badge>
                                <p className="text-xs text-gray-500 mt-1">{doc.expiry_date}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expiring Soon Documents */}
                  {reportData.company_docs_expiring?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-semibold text-amber-700 flex items-center gap-2 mb-3">
                        <Clock className="h-4 w-4" />
                        Expiring Within 45 Days ({reportData.company_docs_expiring.length})
                      </h4>
                      <div className="space-y-2">
                        {reportData.company_docs_expiring.map((doc, idx) => (
                          <div key={idx} className="bg-white rounded p-3 border border-amber-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{doc.document_type}</p>
                                <p className="text-sm text-gray-600">{doc.company_name}</p>
                                {doc.document_number && (
                                  <p className="text-xs text-gray-500">#{doc.document_number}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <Badge className="bg-amber-500">{doc.days_until_expiry}d left</Badge>
                                <p className="text-xs text-gray-500 mt-1">{doc.expiry_date}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <div className="text-right text-gray-500 text-sm">
            <p>Prepared by: <strong>{reportData.prepared_by}</strong></p>
            <p>Report Generated: {reportData.report_date}</p>
          </div>
        </>
      )}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="hr-report-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" />
              Email HR Report — {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </DialogTitle>
            <DialogDescription>
              The report PDF is attached. A copy (CC) goes to HR.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="hr-email-to">Recipient</Label>
              <Input
                id="hr-email-to"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                disabled={emailSending}
                data-testid="hr-report-email-to"
              />
            </div>
            <div>
              <Label htmlFor="hr-email-subject">Subject</Label>
              <Input
                id="hr-email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="(default subject will be used if left blank)"
                disabled={emailSending}
                data-testid="hr-report-email-subject"
              />
            </div>
            <div>
              <Label htmlFor="hr-email-message">Message</Label>
              <Textarea
                id="hr-email-message"
                rows={6}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="(default message will be used if left blank)"
                disabled={emailSending}
                data-testid="hr-report-email-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={emailSending} data-testid="hr-report-email-cancel">Cancel</Button>
            <Button onClick={sendReportEmail} disabled={emailSending || !emailTo.trim()} data-testid="hr-report-email-send">
              {emailSending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-2" /> Send email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRReport;
